import React from 'react';
import { Check, Clock, Construction, Play, Star } from 'lucide-react';
import { NEURO_OPTION_MAP } from 'constants/neuroOptions';
import {
  NEURO_ZONE_META,
  isTrackableDailyActivity,
  type NeuroActivity,
} from 'features/child/data/neuroDashboardContent';
import { useChildProgressStore } from 'features/child/store/childProgressStore';
import { useChildProgressReadAccess } from 'features/child/store/childProgressReadAccess';

interface NeuroZoneCardProps {
  neuroId: string;
  activities: NeuroActivity[];
  onStartActivity: (activity: NeuroActivity) => void;
}

const NeuroZoneCard: React.FC<NeuroZoneCardProps> = ({ neuroId, activities, onStartActivity }) => {
  const option = NEURO_OPTION_MAP[neuroId];
  const meta = NEURO_ZONE_META[neuroId];
  const completions = useChildProgressStore((s) => s.completions);
  const metricValues = useChildProgressStore((s) => s.metricValues);
  const { isReady } = useChildProgressReadAccess();

  const today = new Date().toISOString().slice(0, 10);
  const isCompleted = (activityId: string) =>
    isReady
    && completions.some((c) => c.activityId === activityId && c.completedAt.startsWith(today));
  const metricValue =
    (isReady
      ? metricValues.find((m) => m.neuroId === neuroId && m.date === today)?.value
      : undefined) ?? 0;

  if (!option || !meta) return null;

  const Icon = option.icon;
  const readyActivities = activities.filter((activity) => activity.availability !== 'planned');
  const trackedActivities = readyActivities.filter(isTrackableDailyActivity);
  const doneCount = trackedActivities.filter((activity) => isCompleted(activity.id)).length;

  return (
    <article
      className={`overflow-hidden rounded-[1.75rem] border border-white/70 bg-gradient-to-br ${meta.gradient} ${meta.glow} backdrop-blur-sm dark:border-gray-800`}
    >
      <header className="flex items-start gap-4 border-b border-white/50 p-5 dark:border-gray-800/80 sm:p-6">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/90 shadow-sm dark:bg-gray-900/90">
          {Icon ? <Icon className="h-7 w-7 text-adapt-indigo" aria-hidden /> : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-bold text-adapt-navy dark:text-gray-100">{option.name}</h3>
            <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${meta.chip}`}>
              {option.learningStyle}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-gray-400">{meta.tagline}</p>
          <p className="mt-2 text-xs font-medium text-slate-500">
            {meta.dailyGoalLabel} · {readyActivities.length} tools ready · {trackedActivities.length > 0
              ? `${doneCount}/${trackedActivities.length} tracked activities done`
              : 'no tracked mission in this space today'} · {metricValue} today
          </p>
        </div>
      </header>

      <ul className="space-y-3 p-5 sm:p-6">
        {activities.map((activity) => {
          const planned = activity.availability === 'planned';
          const trackable = !planned && isTrackableDailyActivity(activity);
          const done = trackable && isCompleted(activity.id);
          // Daily recording must not remove access to these support tools.
          const reopenableSupport = activity.neuroId === 'tourettes'
            && (activity.id === 'tourettes-flex-flow' || activity.id === 'tourettes-tic-break');
          const ActivityIcon = activity.icon;

          return (
            <li key={activity.id}>
              <div
                className={`group rounded-2xl border-2 p-4 transition ${
                  planned
                    ? 'border-dashed border-slate-200 bg-white/55 dark:border-gray-700 dark:bg-gray-900/50'
                    : done
                    ? 'border-emerald-300/60 bg-emerald-50/80 dark:border-emerald-800 dark:bg-emerald-950/30'
                    : 'border-white/80 bg-white/85 hover:border-adapt-indigo/30 hover:shadow-md dark:border-gray-700 dark:bg-gray-900/85 dark:hover:border-adapt-cyan/30'
                }`}
              >
                <div className="flex gap-3 sm:gap-4">
                  <div
                    className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
                      planned
                        ? 'bg-slate-100 dark:bg-gray-800'
                        : done
                          ? 'bg-emerald-200/80 dark:bg-emerald-900/50'
                          : 'bg-adapt-indigo/10'
                    }`}
                  >
                    {planned ? (
                      <Construction className="h-5 w-5 text-slate-500" aria-hidden />
                    ) : done ? (
                      <Check className="h-5 w-5 text-emerald-600" aria-hidden />
                    ) : (
                      <ActivityIcon className="h-5 w-5 text-adapt-indigo dark:text-adapt-cyan" aria-hidden />
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-bold text-adapt-navy dark:text-gray-100">{activity.title}</h4>
                      {planned ? (
                        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:bg-gray-800 dark:text-gray-300">
                          Coming next
                        </span>
                      ) : trackable ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                            <Clock className="h-3 w-3" aria-hidden />
                            {activity.durationMinutes} min
                          </span>
                          <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-amber-600">
                            <Star className="h-3 w-3 fill-amber-400 text-amber-400" aria-hidden />
                            +{activity.starsReward}
                          </span>
                        </>
                      ) : (
                        <span className="text-xs font-semibold text-slate-500 dark:text-gray-400">
                          Opens a learning tool
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-600 dark:text-gray-400">{activity.description}</p>
                    {planned && activity.availabilityNote ? (
                      <p className="mt-2 text-xs font-medium text-slate-500 dark:text-gray-400">
                        {activity.availabilityNote}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className="mt-4 flex justify-end">
                  {planned ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-600 dark:bg-gray-800 dark:text-gray-300">
                      <Construction className="h-4 w-4" aria-hidden />
                      Not available yet
                    </span>
                  ) : done ? (
                    <div className="flex flex-wrap items-center justify-end gap-3">
                      <span className="dash-activity-done inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-4 py-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                        <Check className="h-4 w-4" aria-hidden />
                        Completed!
                      </span>
                      {reopenableSupport && <button
                        type="button"
                        disabled={!isReady}
                        onClick={() => onStartActivity(activity)}
                        className="min-h-12 rounded-full border-2 border-purple-300 bg-white px-5 py-3 font-bold text-purple-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-700 dark:border-purple-700 dark:bg-slate-900 dark:text-purple-100"
                      >Use support tool again</button>}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => onStartActivity(activity)}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-adapt-indigo to-adapt-teal px-5 py-2.5 text-sm font-bold text-white shadow-md transition hover:scale-[1.02] hover:shadow-lg"
                    >
                      <Play className="h-4 w-4" aria-hidden />
                      {trackable ? 'Start activity' : 'Open tool'}
                    </button>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </article>
  );
};

export default NeuroZoneCard;
